/**
 * ReportingRegister — the flat, filterable audit register grid at the bottom
 * of the Reporting tab: one row per audit with stage, score, findings by
 * severity, open CAPs, universe attempts, and the key dates. Server-paged at
 * 200 rows per page; the page resets whenever the filters change.
 */
import React, { useEffect, useState } from 'react';
import { Section, useAnalytics, fmtDate } from './ReportingShared.jsx';
import { STAGE_LABELS, PROGRAM_LABELS } from './labels.js';

const PAGE = 200;

function FindingsCell({ r }) {
  const parts = [
    ['findings_critical', 'C', 'text-red-700', 'Critical'],
    ['findings_high', 'H', 'text-orange-700', 'High'],
    ['findings_medium', 'M', 'text-amber-700', 'Medium'],
    ['findings_low', 'L', 'text-slate-500', 'Low'],
  ].filter(([k]) => Number(r[k]) > 0);
  if (!parts.length) return <span className="text-slate-300">0</span>;
  return (
    <span className="whitespace-nowrap">
      {parts.map(([k, letter, cls, title], i) => (
        <span key={k} title={title} className={`font-semibold ${cls}`}>{i > 0 && <span className="text-slate-300"> </span>}{r[k]}{letter}</span>
      ))}
    </span>
  );
}

export default function RegisterSection({ qs }) {
  const [offset, setOffset] = useState(0);
  useEffect(() => { setOffset(0); }, [qs]);
  const fullQs = `${qs ? `${qs}&` : ''}limit=${PAGE}&offset=${offset}`;
  const { loading, error, data } = useAnalytics('register', fullQs);
  const reg = data?.register;
  const rows = reg?.rows || [];
  const total = Number(reg?.total || 0);
  const from = total ? offset + 1 : 0;
  const to = offset + rows.length;
  return (
    <Section
      title="Audit register"
      explainer="Every audit in scope on one flat grid, the row-level record behind all the numbers above."
      loading={loading} error={error} empty={!rows.length}
      emptyText="No audits match these filters."
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="py-1 pr-3">Subject</th>
              <th className="py-1 pr-3">Type</th>
              <th className="py-1 pr-3">Program</th>
              <th className="py-1 pr-3">Lead</th>
              <th className="py-1 pr-3">Stage</th>
              <th className="py-1 pr-3 text-right">Score</th>
              <th className="py-1 pr-3 text-right">Findings</th>
              <th className="py-1 pr-3 text-right">Open CAPs</th>
              <th className="py-1 pr-3 text-right">Attempts</th>
              <th className="py-1 pr-3">Engagement</th>
              <th className="py-1">Closed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100 text-slate-700">
                <td className="py-1.5 pr-3">
                  <div className="font-semibold text-slate-800">{r.subject_name}</div>
                  {r.template_name && <div className="text-[10px] text-slate-400">{r.template_name}</div>}
                </td>
                <td className="py-1.5 pr-3">{r.subject_type === 'fdr' ? 'FDR' : 'Internal'}</td>
                <td className="py-1.5 pr-3 text-slate-500">{r.program ? (PROGRAM_LABELS[r.program] || r.program) : ''}</td>
                <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">
                  {r.lead_auditor_name || <span className="text-slate-300">Unassigned</span>}
                  {Number(r.team_size) > 0 && <span className="text-[10px] text-slate-400"> +{r.team_size}</span>}
                </td>
                <td className="py-1.5 pr-3">{STAGE_LABELS[r.stage] || r.stage}</td>
                <td className="py-1.5 pr-3 text-right font-semibold tabular-nums">{r.compliance_score === null || r.compliance_score === undefined ? <span className="text-slate-300 font-normal">n/a</span> : `${r.compliance_score}%`}</td>
                <td className="py-1.5 pr-3 text-right"><FindingsCell r={r} /></td>
                <td className={`py-1.5 pr-3 text-right tabular-nums ${Number(r.open_caps) > 0 ? 'font-semibold text-amber-700' : 'text-slate-300'}`}>{r.open_caps}</td>
                <td className={`py-1.5 pr-3 text-right tabular-nums ${Number(r.universe_attempts) >= 3 ? 'font-semibold text-red-700' : 'text-slate-500'}`}>{r.universe_attempts}</td>
                <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{fmtDate(r.engagement_date)}</td>
                <td className="py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(r.closed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-[11px] text-slate-500">
        <span>Showing {from} to {to} of {total}</span>
        <div className="flex gap-2">
          <button onClick={() => setOffset(o => Math.max(0, o - PAGE))} disabled={offset === 0}
            className="px-2.5 py-1 font-semibold rounded-lg border border-slate-300 text-slate-600 disabled:opacity-40 hover:bg-slate-50">← Previous</button>
          <button onClick={() => setOffset(o => o + PAGE)} disabled={to >= total}
            className="px-2.5 py-1 font-semibold rounded-lg border border-slate-300 text-slate-600 disabled:opacity-40 hover:bg-slate-50">Next →</button>
        </div>
      </div>
    </Section>
  );
}
